import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { AuthState, User } from '../types'

interface AuthContextType extends AuthState {
  login: () => void
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  })

  useEffect(() => {
    // Check for existing session on app load
    checkExistingSession()
  }, [])

  const checkExistingSession = async () => {
    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.isAuthenticated && data.user) {
          // Get full user profile
          const userResponse = await fetch('/api/auth/me', {
            credentials: 'include'
          })
          
          if (userResponse.ok) {
            const userData = await userResponse.json()
            setAuthState({
              user: userData,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          } else {
            setAuthState(prev => ({ ...prev, isLoading: false }))
          }
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }))
        }
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }))
      }
    } catch (error) {
      console.error('Session check failed:', error)
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Session validation failed'
      })
    }
  }

  const login = () => {
    console.log('🔑 Login function called - redirecting to Google OAuth')
    console.log('🌐 Redirecting to:', '/api/auth/google')
    // Redirect to Google OAuth
    window.location.href = '/api/auth/google'
  }

  const logout = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }))

      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })

      if (response.ok) {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        })
      } else {
        throw new Error('Logout failed')
      }
    } catch (error) {
      console.error('Logout failed:', error)
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Logout failed. Please try again.'
      }))
    }
  }

  const refreshToken = async (): Promise<void> => {
    try {
      // Check session status
      await checkExistingSession()
    } catch (error) {
      console.error('Token refresh failed:', error)
      await logout() // Force re-authentication
    }
  }

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthContext } 